/**
 * useOrderbookSubscriptions Hook
 *
 * Manages on-demand orderbook subscriptions for spot and futures markets.
 *
 * Key Features:
 * - Uses best bid/ask from mark price data for futures (from book ticker stream)
 * - On-demand spot orderbook subscription when modal opens
 * - Automatic cleanup when modal closes
 * - Lifecycle controlled by `enabled` prop
 *
 * Note: Full futures orderbooks are not subscribed per Epic 10.
 * Only best bid/ask from book ticker stream is available.
 * For full orderbook depth, use on-demand REST API fetch (Story 10.4).
 *
 * Usage:
 * ```tsx
 * const { spotOrderbook, futuresOrderbook, loading, error } = useOrderbookSubscriptions(
 *   symbol,
 *   spotExchange,
 *   modalOpen // enabled when modal is open
 * );
 * ```
 */

import { useState, useEffect } from 'react';
import { useFuturesData } from '../contexts/FuturesDataContext';

/**
 * Price level structure
 */
export interface PriceLevel {
  price: number;
  volume: number;
  total: number;
}

/**
 * Orderbook structure
 */
export interface OrderBook {
  bids: PriceLevel[];
  asks: PriceLevel[];
  timestamp: number;
}

/**
 * Hook return type
 */
interface UseOrderbookSubscriptionsReturn {
  spotOrderbook: OrderBook | null;
  futuresOrderbook: OrderBook | null;
  loading: boolean;
  error: string | null;
}

/**
 * On-Demand Orderbook Subscription Hook
 *
 * @param symbol - Trading pair symbol (e.g., "BTCUSDT")
 * @param spotExchange - Spot exchange name
 * @param enabled - Whether subscription is active (true when modal open)
 * @returns Orderbook data, loading state, and errors
 */
export function useOrderbookSubscriptions(
  symbol: string,
  spotExchange: string,
  enabled: boolean
): UseOrderbookSubscriptionsReturn {
  // Get mark prices from FuturesDataContext (includes bestBid/bestAsk from book ticker)
  const { markPrices } = useFuturesData();

  // Local state for spot orderbook
  const [spotOrderbook, setSpotOrderbook] = useState<OrderBook | null>(null);
  const [loading, setLoading] = useState(true);
  const [error] = useState<string | null>(null);

  /**
   * Spot orderbook effect
   * TODO: Implement on-demand CCXT subscription for spot exchanges
   * For now, returns mock data for UI development
   */
  useEffect(() => {
    if (!enabled) {
      // Clear spot orderbook when modal closes
      setSpotOrderbook(null);
      setLoading(true);
      return;
    }

    // Mock subscription for development
    // TODO: Replace with actual CCXT watchOrderBook() implementation
    console.log(`[useOrderbookSubscriptions] Mock subscribing to ${spotExchange} ${symbol}`);

    // Simulate loading delay
    setLoading(true);
    const loadingTimer = setTimeout(() => {
      // Generate mock orderbook data
      setSpotOrderbook({
        bids: Array.from({ length: 10 }, (_, i) => ({
          price: 50000 - i * 10,
          volume: Math.random() * 5,
          total: Math.random() * 10,
        })),
        asks: Array.from({ length: 10 }, (_, i) => ({
          price: 50100 + i * 10,
          volume: Math.random() * 5,
          total: Math.random() * 10,
        })),
        timestamp: Date.now(),
      });
      setLoading(false);
    }, 500);

    // Cleanup function - unsubscribe when modal closes
    return () => {
      console.log(`[useOrderbookSubscriptions] Cleanup - unsubscribing from ${spotExchange} ${symbol}`);
      clearTimeout(loadingTimer);
      setSpotOrderbook(null);
      setLoading(true);
    };
  }, [enabled, symbol, spotExchange]);

  /**
   * Get futures orderbook from best bid/ask
   * Note: Only top-of-book is available from book ticker stream
   * For full depth, use on-demand REST API fetch (Story 10.4)
   */
  const getFuturesOrderbook = (): OrderBook | null => {
    if (!enabled) {
      return null;
    }

    const markPrice = markPrices.get(symbol);
    if (!markPrice || !markPrice.bestBid || !markPrice.bestAsk) {
      console.warn(`[useOrderbookSubscriptions] Futures mark price not found for ${symbol}`);
      return null;
    }

    // Create a minimal orderbook with just top-of-book from book ticker
    // Note: Full depth is not available - only best bid/ask
    return {
      bids: [{
        price: markPrice.bestBid,
        volume: markPrice.bestBidQty || 0,
        total: markPrice.bestBidQty || 0,
      }],
      asks: [{
        price: markPrice.bestAsk,
        volume: markPrice.bestAskQty || 0,
        total: markPrice.bestAskQty || 0,
      }],
      timestamp: markPrice.timestamp,
    };
  };

  return {
    spotOrderbook,
    futuresOrderbook: getFuturesOrderbook(),
    loading,
    error,
  };
}
